import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  JourneyEvent,
  deleteJourneyEvent,
  deleteJourneyProximity,
  deleteJourneyTransaction,
  deleteProximityTimeRange,
  deleteTrackPersistence,
  selectActiveJourneyProximities,
  selectActiveJourneyTransactions,
  selectActiveProximityTimeRanges,
  selectActiveTrackPersistences,
  selectAnnotationData,
  selectAnnotationView,
  selectIsEditable,
  selectSelectedJourneyEvents,
  setCurrentCamera,
  setGondolaByName,
  setGondolaDropdownValue,
  setGondolaDropdownValueByName,
  setHighlightedTimelineEventId,
  setSelectedEvent,
  setSelectedGondola,
} from "@/entities/annotation";
import {
  JourneyProximity,
  JourneyTransaction,
  ProximityTimeRange,
  TrackPersistence,
} from "@/shared/api/lod-api";
import { useMemo } from "react";
import { toast } from "react-hot-toast";
import JourneyAnnotationItem from "./journey-annotation-item";
import { Box, Flex, ScrollArea, Text } from "@radix-ui/themes";
import { Panel } from "react-resizable-panels";
import { formatEventData } from "../lib/event-utils";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import { useCameras } from "../hooks/useCameras";
import { allOption } from "./gondola-select";
import { useTimelineData } from "../hooks/useTimelineData";
import { usePlayer } from "@tezzro/player";

export function JourneyEvents() {
  const dispatch = useAppDispatch();
  const { removeTimelineAnnotationEvent } = useTimelineData();

  const selectedJourneyEvents = useAppSelector((state) =>
    selectSelectedJourneyEvents(state)
  );
  const activeJourneyTransactions = useAppSelector(
    selectActiveJourneyTransactions
  );
  const activeTrackPersistences = useAppSelector(selectActiveTrackPersistences);
  const activeJourneyProximities = useAppSelector(
    selectActiveJourneyProximities
  );
  const activeProximityTimeRanges = useAppSelector(
    selectActiveProximityTimeRanges
  );
  const annotationView = useAppSelector(selectAnnotationView);
  const annotationData = useAppSelector(selectAnnotationData);

  const allEvents = useMemo(
    () => [
      ...selectedJourneyEvents,
      ...activeJourneyTransactions,
      ...activeTrackPersistences,
      ...activeJourneyProximities,
      ...activeProximityTimeRanges,
    ],
    [
      selectedJourneyEvents,
      activeJourneyTransactions,
      activeTrackPersistences,
      activeJourneyProximities,
      activeProximityTimeRanges,
    ]
  );

  const selectedEvent = useAppSelector(
    (state) => state.annotation.selectedEvent
  );
  const player = usePlayer();
  const isEditable = useAppSelector(selectIsEditable);

  const cameraData = useCameras();

  const onDelete =
    (
      event:
        | JourneyEvent
        | JourneyTransaction
        | TrackPersistence
        | JourneyProximity
        | ProximityTimeRange
    ) =>
    (mouseEvent: React.MouseEvent) => {
      mouseEvent.stopPropagation();

      switch (event.type) {
        case ANNOTATION_TYPES.JOURNEY:
          dispatch(deleteJourneyEvent({ type: event.eventType }));
          break;
        case ANNOTATION_TYPES.TRANSACTION:
          dispatch(deleteJourneyTransaction(event.id));
          break;
        case ANNOTATION_TYPES.TRACK_PERSISTENCE:
          dispatch(deleteTrackPersistence(event.id));
          break;
        case ANNOTATION_TYPES.PROXIMITY:
          dispatch(deleteJourneyProximity(event.id));
          break;
        case ANNOTATION_TYPES.PROXIMITY_TIME_RANGES:
          dispatch(deleteProximityTimeRange(event.id));
          break;
      }

      dispatch(setSelectedEvent(null));
      dispatch(setHighlightedTimelineEventId(null));
      toast.success("Event deleted successfully");
      removeTimelineAnnotationEvent?.({
        id: event.id,
        type: event.type,
        journeyId: event.journeyId,
        timestamp: new Date(event?.timestamp)?.toISOString(),
      });
    };

  const selectGondola = (gondolaName: string | null) => {
    if (gondolaName) {
      dispatch(setGondolaByName(gondolaName));
      dispatch(setGondolaDropdownValueByName(gondolaName));
    } else {
      dispatch(setSelectedGondola(null));
      dispatch(setGondolaDropdownValue(allOption));
    }
  };

  const onSelect = ({
    event,
    timestamp,
    cameraId,
  }: {
    event:
      | JourneyEvent
      | JourneyTransaction
      | TrackPersistence
      | JourneyProximity
      | ProximityTimeRange;
    timestamp: Date;
    cameraId: string;
  }) => {
    if (annotationData) {
      toast.error("Complete the current annotation before proceeding");
      return;
    }

    if (selectedEvent?.id !== event.id) {
      player.seek(timestamp, cameraId);
      dispatch(setSelectedEvent(event));
      dispatch(setHighlightedTimelineEventId(event.id));
      dispatch(setCurrentCamera(cameraId));
      cameraData.setCameraModeFromCameraId(cameraId);

      if (
        event.type === ANNOTATION_TYPES.JOURNEY ||
        event.type === ANNOTATION_TYPES.TRACK_PERSISTENCE
      ) {
        const gondolaName = cameraData?.findCameraGondolaName?.(
          event.cameraId as string
        );
        selectGondola(gondolaName);
      } else if (
        [
          ANNOTATION_TYPES.TRANSACTION,
          ANNOTATION_TYPES.PROXIMITY,
          ANNOTATION_TYPES.PROXIMITY_TIME_RANGES,
        ].includes(event?.type)
      ) {
        selectGondola(event.gondolaName);
      }
    } else {
      dispatch(setSelectedEvent(null));
      dispatch(setHighlightedTimelineEventId(null));
    }
  };

  const canBeDeleted = (
    event:
      | JourneyEvent
      | JourneyTransaction
      | TrackPersistence
      | JourneyProximity
      | ProximityTimeRange
  ) => {
    if (!isEditable) {
      return false;
    }

    if (
      annotationView === ANNOTATION_TYPES.JOURNEY &&
      event.type === ANNOTATION_TYPES.JOURNEY
    ) {
      return true;
    }

    if (
      annotationView === ANNOTATION_TYPES.TRANSACTION &&
      event.type === ANNOTATION_TYPES.TRANSACTION
    ) {
      return true;
    }

    if (
      annotationView === ANNOTATION_TYPES.TRACK_PERSISTENCE &&
      event.type === ANNOTATION_TYPES.TRACK_PERSISTENCE
    ) {
      return true;
    }

    if (
      annotationView === ANNOTATION_TYPES.PROXIMITY &&
      event.type === ANNOTATION_TYPES.PROXIMITY
    ) {
      const hasProximityTimeRanges = activeProximityTimeRanges.some(
        (item) => item.proximityId === event.id
      );

      return hasProximityTimeRanges ? false : true;
    }

    if (
      annotationView === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES &&
      event.type === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES
    ) {
      return true;
    }

    return false;
  };

  return (
    <Panel id="journey-events" defaultSize={50}>
      <Box className="h-full">
        <Text size="2" weight="bold" as="p" m="2">
          Journey annotations
        </Text>
        <ScrollArea className="h-[calc(100%-40px)] annotation-items">
          <Flex direction="column" gap="2">
            {allEvents?.length
              ? allEvents
                  .sort(
                    (a, b) =>
                      new Date(a.timestamp).getTime() -
                      new Date(b.timestamp).getTime()
                  )
                  .map((event) => {
                    const { title, timestamp, cameraId } =
                      formatEventData(event);
                    const isDeletable = canBeDeleted(event);

                    return (
                      <JourneyAnnotationItem
                        key={event.id}
                        title={title}
                        selectedEventId={event.id}
                        timestamp={timestamp}
                        isEditable={isDeletable}
                        isSelected={selectedEvent?.id === event.id}
                        onSelect={() =>
                          onSelect({
                            event,
                            timestamp,
                            cameraId,
                          })
                        }
                        onDelete={(mouseEvent) => onDelete(event)(mouseEvent)}
                      />
                    );
                  })
              : null}
          </Flex>
        </ScrollArea>
      </Box>
    </Panel>
  );
}
